import { createServerFn } from '@tanstack/react-start'
import { requireAuth, requireScopes, rateLimitFunction, requireOrgTokenToMatchOrgId, createPostHogTrackingMiddleware } from '~/lib/middleware'
import { ScopeRule } from '~/lib/scopes'
import { LogtoScopes } from '~/lib/logto/logto'
import { ApiResponse, ApiKey, apiKeySchema } from '~/lib/schemas'
import z from 'zod'

export const createApiKeySchema = z.object({
  organization_id: z.string().min(1, 'Organization ID is required'),
  name: z.string().min(1, 'API key name is required').max(100, 'API key name must be less than 100 characters'),
  description: z.string().optional(),
  expires_in: z.number().optional(),
  scopes: z.array(z.string()).optional(),
})

export type CreateApiKeyRequest = z.infer<typeof createApiKeySchema>

export const createApiKey = createServerFn({
  method: 'POST',
})
  .middleware([
    requireAuth,
    rateLimitFunction,
    requireScopes(ScopeRule.OR([LogtoScopes.AdminOrgKeysCrud, ScopeRule.ORG(LogtoScopes.OrgKeyCrud)])),
    requireOrgTokenToMatchOrgId,
    createPostHogTrackingMiddleware('create_api_key')
  ])
  .validator(createApiKeySchema)
  .handler(async (ctx) => {
    const { organization_id, name, description, expires_in, scopes } = ctx.data
   
    // Use real API
    const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
    const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
    
    if (!BFF_API_KEY) {
      throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
    }
    
    const requestBody = {
      organization_id,
      name,
      description,
      expires_in,
      scopes,
    }
    
    const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/api-keys`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${BFF_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }
    
    const result = await response.json() as ApiResponse<ApiKey>
    return apiKeySchema.parse(result.data)
  }) 
